import Kalvium from '@kalvi-education/kalvium-sdk'
import dayjs from 'dayjs'

import AX_INSTRUCTIONS_ACTION from '../../enums/axInstructionsActions.js'
import AX_INSTRUCTIONS_STATUS from '../../enums/axInstructionsStatus.js'
import AX_INSTRUCTIONS_STEPS from '../../enums/axInstructionsSteps.js'
import PROCTORING_TYPE from '../../enums/proctoringType.js'
import {
    clearRegularAssessmentAttemptStorage,
    REGULAR_ASSESSMENT_SOURCE_FLOW,
} from '../../utils/regularAssessmentStorage.js'
import { applyPreviewAssessmentConfig } from '../../utils/previewAssessment.js'
import { clearSharedAttemptStorageAfterClose } from '../../utils/sharedAttemptStorage.js'

const handleError = (error, toast, navigate) => {
    switch (error.message) {
        case 'Assessment not found':
            toast({
                title: `Assessment not found`,
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
            })
            navigate('/404')
            break
        case 'jwt malformed':
            toast({
                title: `Please login to view the page`,
                status: 'info',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
            })
            // this is show the login component
            break
        case 'Access denied':
            navigate('/404')
            break
        default:
            toast({
                title: error.message,
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
            })
            navigate('/404')
            break
    }
}

const handleProctoringAndAllowExtra = (
    steps,
    isProctoringDisabled,
    isAllowExtraDisabled
) => {
    switch (true) {
        case !isAllowExtraDisabled:
            break
        case isAllowExtraDisabled:
            steps = steps.filter(
                step => step.name !== AX_INSTRUCTIONS_STEPS.DETAILS_FORM
            )
            break
    }
    steps[0].status = AX_INSTRUCTIONS_STATUS.CURRENT
    return steps
}

const getAssessmentDetails = async (slug, steps, dispatch, setAssessment, isPreview = false) => {
    const rawAssessmentData =
        await Kalvium.services.assessment.fetchAssessmentDetails(slug)
    const assessmentData = applyPreviewAssessmentConfig(rawAssessmentData, isPreview)

    const payloadList = await handleProctoringAndAllowExtra(
        steps,
        !(assessmentData.proctoring === PROCTORING_TYPE.REMOTE_PROCTORING),
        !assessmentData.allow_extra
    )

    dispatch({
        type: AX_INSTRUCTIONS_ACTION.UPDATE_STEPS,
        payload: payloadList,
    })

    setAssessment(assessmentData)
    // save assessment to store
    dispatch({
        type: AX_INSTRUCTIONS_ACTION.UPDATE_ASSESSMENT,
        payload: assessmentData,
    })
    return assessmentData
}

const handleExistingAttempts = (attempts,axData,navigate,slug,dispatch) => {
    attempts.sort((a, b) =>
        dayjs(a?.start_time).isAfter(dayjs(b?.start_time)) ? -1 : 1
    )

    // if the latest attempt is closed, take to attempt page
    if (attempts[0]?.closed && attempts[0]?.id) {
        clearRegularAssessmentAttemptStorage(slug)
        clearSharedAttemptStorageAfterClose({
            slug,
            attemptId: attempts[0].id,
        })
        const canRetryNormally =
            axData?.retry !== null &&
            dayjs().isAfter(
                dayjs(attempts[0]?.closed_at).add(
                    axData?.retry?.delay,
                    'minutes'
                )
            )
        const hasApprovedChangeRequest = axData?.hasApprovedChangeRequest
        if (canRetryNormally || hasApprovedChangeRequest) {
            dispatch({
                type: AX_INSTRUCTIONS_ACTION.UPDATE_RETRY_ENABLED,
                value: true,
            })
            return
        } else {
            navigate(`/assessment/${slug}/attempt/${attempts[0].id}`, {
                state: {
                    attemptData: attempts[0],
                    sourceFlow: REGULAR_ASSESSMENT_SOURCE_FLOW,
                },
            })
            return
        }
    }
    // else if existing attempts are not closed
    const payloadList =
        ( axData.proctoring === PROCTORING_TYPE.REMOTE_PROCTORING || axData.full_screen_mode === true ) 
            ? [
                  {
                      name: AX_INSTRUCTIONS_STEPS.INSTRUCTIONS,
                      status: AX_INSTRUCTIONS_STATUS.COMPLETED,
                  },
                  {
                      name: AX_INSTRUCTIONS_STEPS.SETUP_TEST_ENVIRONMENT,
                      status: AX_INSTRUCTIONS_STATUS.CURRENT,
                  },
              ]
            : [
                  {
                      name: AX_INSTRUCTIONS_STEPS.INSTRUCTIONS,
                      status: AX_INSTRUCTIONS_STATUS.CURRENT,
                  },
              ]

    dispatch({
        type: AX_INSTRUCTIONS_ACTION.UPDATE_STEPS,
        payload: payloadList,
    })
    dispatch({
        type: AX_INSTRUCTIONS_ACTION.NEXT_STEP,
    })
}

const getExistingAttempts = async (slug, axData, navigate, dispatch, isPreview) => {
    const { list } = await Kalvium.services.assessment.getAxAttempts(slug)
    const attempts = (list ?? []).filter(attempt =>
        isPreview
            ? attempt?.extras?.is_preview === true
            : attempt?.extras?.is_preview !== true
    )

    dispatch({
        type: AX_INSTRUCTIONS_ACTION.UPDATE_ATTEMPTS,
        attempts,
    })

    if (attempts?.length) {
        handleExistingAttempts(attempts, axData, navigate, slug, dispatch)
    }
}

const getAxData = async (
    slug,
    steps,
    dispatch,
    toast,
    navigate,
    setAssessment,
    setIsLoading,
    isPreview = false
) => {
    getAssessmentDetails(slug, steps, dispatch, setAssessment, isPreview)
        .then(axData =>
            getExistingAttempts(slug, axData, navigate, dispatch, isPreview)
        )
        .catch(err => handleError(err, toast, navigate))
        .finally(() => {
            setIsLoading(false)
        })
}

export { getAxData }
